import crypto from 'crypto';
import moment from 'moment';
import randomstring from 'randomstring';
import config from '../../config';

import storage, { storagePersistent } from '../../util/storage';
import callApi from '../../util/apiCaller';
import { slugify } from '../../util/text';

import { redirect, initApp, setIsFetching, FETCH_LIMIT } from '../App/AppActions';
import { initLanguage } from '../Intl/IntlActions';
import { addError } from '../Error/ErrorActions';
import { trackSignup } from '../../util/metaTracking.js';
import { getOrdersByUser } from '../Order/OrderActions';
import { getConversationOption, getConversations } from '../Conversation/ConversationActions';

export const USERS_FETCH_LIMIT = 1000;

// Export Constants
export const SET_VISITOR = 'SET_VISITOR';
export const REGISTER_USER = 'REGISTER_USER';
export const LOGIN_USER = 'LOGIN_USER';
export const LOGOUT_USER = 'LOGOUT_USER';
export const CONNECTED_USER = 'CONNECTED_USER';
export const CONNECTED_USERS = 'CONNECTED_USERS';
export const DISCONNECTED_USER = 'DISCONNECTED_USER';
export const SET_USERS = 'SET_USERS';
export const SET_USER_ACTIVITY = 'SET_USER_ACTIVITY';
export const SET_USER_ACTIVITY_DATE = 'SET_USER_ACTIVITY_DATE';
export const SET_SUBSCRIPTIONS = 'SET_SUBSCRIPTIONS';
export const SET_NEED_PREPAYMENT = 'SET_NEED_PREPAYMENT';

export const USER_EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/m;
export const USER_PHONE_REGEX = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/gmi;
// export const USER_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[0-9])(?=.*\W).{6,64}$/i; // 6 chars + 1 number + 1 special character
export const USER_PASSWORD_REGEX = /^(.){6,64}$/i;
export const USER_ACTIVITY_THRESHOLD = 5; // in minutes

export const LOGIN_TOKEN_NAME = `${slugify((config.application.name || '').toLowerCase())}.${process.env.NODE_ENV}.auth`;

// Export Actions
export function isLoggedInRequest() {
    return dispatch => {
        if(hasAuthToken()) {
            return callApi('user/getloggeduser').then(res => {
                if(res.user) {
                    dispatch(loginUser(res.user));
                    dispatch(initLanguage(res.user.preferences.language, res.user));
                    dispatch(redirect());
                } else {
                    dispatch(logoutUser());
                }
                return res.user;
            }).catch(error => {
                if(error && error.message !== 'Unauthorized') {
                    dispatch(addError(error));
                }
                dispatch(logoutUser());
                return false;
            });
        }
        return Promise.resolve(false);
    };
}

export function checkUserAccountRequest(email) {
    return dispatch => {
        return callApi('user/accountexists', 'post', { user: { email } }).then(result => {
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function loginRequest(email, password) {
    return dispatch => {
        return callApi('user/login', 'post', { email, password }).then(result => {
            if(result.token) {
                window.dataLayer?.push({ event: 'login' });
            }
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function socialLoginRequest(provider, idToken, extra = {}) {
    return dispatch => {
        return callApi(`user/auth/${provider}`, 'post', {
            ...(idToken ? { idToken } : {}),
            ...extra,
        }).then(result => {
            if(result && result.token) {
                if(result.isNew && result.user) {
                    trackSignup(result.user);
                } else {
                    window.dataLayer?.push({ event: 'login' });
                }
                return dispatch(login(result.token)).then(() => result);
            }
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function bridgeCodeRequest(challenge, client) {
    return dispatch => {
        return callApi('user/auth/bridge/code', 'post', { challenge, client }).then(result => {
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function socialLinkRequest(provider, idToken, extra = {}) {
    return dispatch => {
        return callApi(`user/auth/${provider}/link`, 'post', {
            ...(idToken ? { idToken } : {}),
            ...extra,
        }).then(result => {
            if(result && result.user) {
                dispatch(loginUser(result.user));
            }
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function socialUnlinkRequest(provider) {
    return dispatch => {
        return callApi(`user/auth/${provider}`, 'delete').then(result => {
            if(result && result.user) {
                dispatch(loginUser(result.user));
            }
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function loginByIdRequest(userId) {
    return dispatch => {
        return callApi('user/login/id', 'post', { user: userId }).then(result => {
			dispatch(login(result.token));
            return result;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

/**
 * Adresse inconnue : compte créé et session ouverte ({ user, token, isNew }).
 * Compte client existant (allowExisting) : { exists: true }, un code part par email,
 * redirect est la page du lien de secours de ce même email.
 */
export function registerRequest(user, allowExisting = true, redirect = '') {
    return dispatch => {
        return callApi('user/register', 'post', { user, allowExisting, ...(redirect ? { redirect } : {}) }).then(result => {
			if(result && result.token) {
				return dispatch(login(result.token)).then(() => {
                    if(result.isNew && result.user) {
                        trackSignup(result.user);
                    }
					return result;
				});
			}
            return result;
        }).catch(err => {
            console.error(err);
            dispatch(addError(err));
            return null;
        });
    };
}

/** Code à 6 chiffres reçu par email : { token, user } si le code est bon, sinon rejet { message }. */
export function loginWithCodeRequest(email, code) {
    return dispatch => callApi('user/login/code', 'post', { email, code }).then(result => {
        if(result && result.token) {
            window.dataLayer?.push({ event: 'login' });
            return dispatch(login(result.token)).then(() => result);
        }
        return result;
    });
}

/** Nouveau code et nouveau lien par email (réponse identique que le compte existe ou non). */
export function requestLoginCodeRequest(email, redirect, language) {
    return callApi('user/login/code/send', 'post', { email, redirect, language });
}

export function askLostPasswordRequest(email) {
    return dispatch => {
        const salt = randomstring.generate();
        const hash = crypto
            .createHmac('sha256', salt)
            .update(email)
            .digest()
            .toString('base64');

        return callApi('user/lostpassword', 'post', { email, salt, hash }).then(result => {
            return result && result.ok;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function resetPasswordRequest(email, code, password) {
    return dispatch => {
        const salt = randomstring.generate();
        const hash = crypto
            .createHmac('sha256', salt)
            .update(`${email}.${code}`)
            .digest()
            .toString('base64');

        return callApi('user/resetpassword', 'post', { email, code, password, salt, hash }).then(result => {
            return result && result.ok;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function editPasswordRequest(oldPassword, newPassword) {
    return dispatch => {
        return callApi('user/editpassword', 'post', { oldPassword, newPassword }).then(result => {
            return result && result.ok;
        }).catch(err => {
            console.error(err);
            return null;
        });
    };
}

export function getUsersRequest(search = '', filters = null, sorter = { column: null, type: 'ASC' }, pager = { current: 0, size: FETCH_LIMIT }) {
    const recursiveFetch = (limit, start = 0, items = []) => {
        return new Promise((resolve, reject) => {
            callApi(`users?${search ? `search=${search}&` : ''}${filters ? `${Object.keys(filters).map(filterKey => `filters[${filterKey}]=${filters[filterKey]}`).join('&')}&` : ''}${sorter && sorter.column ? `sortBy=${sorter.column}&sortType=${sorter.type || 'ASC'}` : ''}&start=${start}&limit=${limit}`).then(res => {
                items = items.concat(res.users);
                if(!pager && res.users && res.users.length >= FETCH_LIMIT) {
                    return resolve(recursiveFetch(limit, start + limit, items));
                }
                resolve(items);
            }).catch(err => {
                reject(err);
            });
        });
    };

    return dispatch => {
        dispatch(setIsFetching('users'));
        return recursiveFetch((pager && pager.size) || FETCH_LIMIT, pager ? pager.current * pager.size : 0).then(users => {
            dispatch(setUsers(users || []));
            return users;
        }).catch(err => {
            dispatch(addError(err));
            return null;
        });
    };
}

export function searchUsersRequest(search) {
    return dispatch => {
        return callApi('users/search', 'post', { search }).then(res => {
            dispatch(setUsers(res.users || []));
            return res.users;
        }).catch(error => {
            dispatch(addError(error));
        });
    };
}

export function getUserRequest(userId) {
	console.log('fetch user', userId);
    return dispatch => {
        return callApi(`user/${userId}`).then(res => {
            dispatch(setUsers([res.user || {}]));
            return res.user;
        }).catch(error => {
            dispatch(addError(error));
        });
    };
}

export function editUserRequest(user, isLoggedUser = false, requestAll = true) {
    return dispatch => {
        return callApi('user/edit', 'post', { user }).then(res => {
            if(res.user && isLoggedUser) {
                dispatch(loginUser(res.user));
            } else if(requestAll) {
                dispatch(getUsersRequest());
            } else {
				dispatch(getUserRequest(user._id));
			}
            return res.user;
        }).catch(error => {
            dispatch(addError(error));
            return null;
        });
    };
}

function postBookIntent(payload) {
    return dispatch => {
        return callApi('user/intent', 'post', payload).then(res => {
            if(res && res.user) {
                dispatch(loginUser(res.user));
                return true;
            }
            return false;
        }).catch(() => false);
    };
}

export function saveBookIntentRequest(payload) {
    return postBookIntent(payload);
}

export function refreshLoggedUserRequest() {
    return dispatch => {
        if(!hasAuthToken()) {
            return Promise.resolve(null);
        }
        return callApi('user/getloggeduser').then(res => {
            if(res && res.user) {
                dispatch(loginUser(res.user));
                return res.user;
            }
            return null;
        }).catch(() => null);
    };
}

export function resendConfirmationRequest() {
    return dispatch => {
        return callApi('user/confirm-email/resend', 'post', {}).then(res => {
            if(res && res.user) {
                dispatch(loginUser(res.user));
            }
            return res;
        }).catch(err => {
            dispatch(addError(err));
            return null;
        });
    };
}

export function recordInboxPromptRequest(action, context) {
    return dispatch => {
        return callApi('user/inbox-prompt', 'post', { action, context }).then(res => {
            if(res && res.user) {
                dispatch(loginUser(res.user));
                return res.user;
            }
            return null;
        }).catch(() => null);
    };
}

export function syncBookIntentFromOrderRequest() {
    return postBookIntent({ syncFromOrder: true });
}

export function recordMessengerIntentRequest(messenger, device = null) {
    return (dispatch, getState) => {
        const user = getLoggedUser(getState());
        if(!user || (user.role && user.role !== 'customer')) {
            return Promise.resolve(null);
        }
        const prefs = user.preferences || {};
        const sameChannel = !prefs.firstMessenger || prefs.firstMessenger === messenger;
        const needMessenger = Boolean(messenger) && !prefs.firstMessenger;
        const needDevice = Boolean(device) && !prefs.firstMessengerDevice && sameChannel;
        if(!needMessenger && !needDevice) {
            return Promise.resolve(null);
        }
        const payload = {};
        if(messenger) {
            payload.messenger = messenger;
        }
        if(needDevice) {
            payload.device = device;
        }
        return dispatch(postBookIntent(payload));
    };
}

export function setUserPreferenceRequest(preferenceKey, preferenceValue, isLoggedUser = true) {
    return dispatch => {
        return callApi(`user/preference/${preferenceKey}/${encodeURIComponent(preferenceValue)}`).then(res => {
            res.user && isLoggedUser && dispatch(loginUser(res.user));
            res.user && !isLoggedUser && dispatch(getUsersRequest());
            return res.user;
        }).catch(error => {
            // dispatch(addError(error));
        });
    };
}

export function subscribeNewsletterRequest(newsletter = 'general', isSubscribe = true, user = {}, blockLogin = false) {
    return dispatch => {
        return callApi(`user/newsletter/subscribe/${newsletter}`, 'post', { isSubscribing: isSubscribe, user }).then(res => {
            if(res.ok && !blockLogin) {
                dispatch(setSubscriptions(res.subscriptions));
                dispatch(loginUser({ ...user, newsletterSubscriptions: res.subscriptions }));
                dispatch(isLoggedInRequest());
            }
            return res.ok;
        }).catch(error => {
            // dispatch(addError(error));
            return null;
        });
    };
}

export function removeUserRequest(userId) {
    return dispatch => {
        return callApi('user/remove', 'delete', { user: { _id: userId } }).then(res => {
            if(res.ok) {
                dispatch(getUsersRequest());
            }
        }).catch(error => {
            dispatch(addError(error));
        });
    };
}

export function deleteAccountRequest() {
    return dispatch => {
        return callApi('user/deleteaccount', 'delete').then(res => {
            return res.ok;
        }).catch(error => {
            dispatch(addError(error));
        });
    };
}

export function sendNewAccountEmailRequest(userId) {
    return dispatch => {
        return callApi(`user/alert/${userId}`).then(res => {
            return res.ok;
        }).catch(error => {
            // dispatch(addError(error));
            return null;
        });
    };
}

export function hasAuthToken() {
    return !!getAuthToken();
}

export function getAuthToken() {
    return storagePersistent && storagePersistent.getItem(LOGIN_TOKEN_NAME);
}

export function login(token) {
    return dispatch => {
        if(storagePersistent) {
            storagePersistent.setItem(LOGIN_TOKEN_NAME, token);
        }
        // dispatch(isLoggedIn());
        return dispatch(initApp());
    };
}

export function isLoggedIn() {
    return dispatch => {
        return dispatch(isLoggedInRequest());
    };
}

// Getters && Check
export function checkEmail(email) {
    return USER_EMAIL_REGEX.test(email);
}

export function checkPhone(phone) {
    return USER_PHONE_REGEX.test((phone || '').trim().replace(/\s/g, '').toLowerCase());
}

export function checkPassword(password) {
    return USER_PASSWORD_REGEX.test(password);
}

export function getUserPreference(user, preference) {
    return (user && user.preferences && user.preferences[preference]) || '';
}

export function getUserOption(user, option) {
    return (user && user.options && user.options[option]) || '';
}

export function getLoggedUser(store) {
    return store.users.user;
}

export function getUsers(store, roleFilter = null, isActive = null) {
    return store.users.data
        .filter(user => !roleFilter || roleFilter.includes(user.role))
        .filter(user => isActive === null || user.isActive === isActive);
}

export function getUser(store, userId) {
    console.log('User::: Search for user', userId, store.users.data);
    return store.users.data.find(user => user._id === userId);
}

export function getUserActivity(store, userId) {
    return store.users.connectedUsers[userId];
}

export function getUserDateActivity(store, key) {
    return (store.users.activityDates && store.users.activityDates[key]) || null;
}

export function doesUserNeedPrePayment(store) {
	const user = getLoggedUser(store);
	const orders = getOrdersByUser(store, user?._id, ['onpreparation', 'shipped', 'complete', 'archived']);
	const conversations = getConversations(store, user);
	return (
		!!store.users.needPrePayment
		|| getUserOption(user, 'needPrePayment') // set on user option
	) && (
		!orders?.length
		|| !orders.find(order => order.items.find(item => item.options?.useForCredit))
	) && !conversations.length;
}

export function getUserId(userOrUserId) {
    return (userOrUserId || {})._id || userOrUserId;
}

export function isUserSubscribedToNewsletter(user, name, defaultValueIfMissing = false) {
    const subscription = user && (user.newsletterSubscriptions || []).find(sub => sub.name === name);
    if(subscription) {
        return subscription.status === 'subscribed';
    }
    return defaultValueIfMissing;
}

export function doesUserCanEditMessages(user, conversation = null) {
	return getUserOption(user, 'canCustomizeMessages') || getConversationOption(conversation, 'canCustomizeMessages');
}

export function getAvailableUserRoles() {
    return ['customer', 'preparator', 'printer', 'manager', 'admin'];
}

export function getUserRolesForConsole() {
    return ['printer', 'manager', 'admin'];
}

// Actions
export function loginUser(user) {
    // console.log('set login user', user);
    // token = token.replace('JWT ', '');
    return {
        type: LOGIN_USER,
        user,
        // token: token
    };
}

export function logoutUser(userAction = false) {
    if(userAction) {
        storagePersistent && storagePersistent.removeItem(LOGIN_TOKEN_NAME);
        storage && storage.removeItem('userEmail');
    }
    return {
        type: LOGOUT_USER,
    };
}

// export function userConnected(user) {
//     return {
//         type: CONNECTED_USER,
//         user,
//     };
// }
//
// export function usersConnected(users) {
//     return {
//         type: CONNECTED_USERS,
//         users,
//     };
// }
//
// export function userDisconnected(user) {
//     return {
//         type: DISCONNECTED_USER,
//         user,
//     };
// }

export function setUsers(users) {
    return {
        type: SET_USERS,
        users,
    };
}

export function setUserActivity(userId) {
    return {
        type: SET_USER_ACTIVITY,
        userId,
        lastActivityTimestamp: moment().unix(),
    };
}

export function setUserDateActivity(key, value) {
    return {
        type: SET_USER_ACTIVITY_DATE,
        key,
        value: value || moment.utc().unix(),
    };
}

export function setSubscriptions(subscriptions) {
    return {
        type: SET_SUBSCRIPTIONS,
        subscriptions,
    };
}

export function setNeedPrePayment(needPrePayment = true) {
	return {
		type: SET_NEED_PREPAYMENT,
		needPrePayment,
	};
}
